import { useState } from 'react';
import { calendarApi } from '../../api/client';
import { Button, Card, Chip, Field, Notice } from '../../components/ui';
import { shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → System → Google Calendar (1.13.0, Teampace 2709): Ceexo's Google sign-in app (its
 * OAuth client), so people can connect their Google Calendar to meetings. The secret is sealed with
 * DATA_ENCRYPTION_KEY when it's saved and never shown again. The redirect address to give Google is
 * shown here, from PUBLIC_URL.
 */
export function GoogleCalendarCard() {
  const view = useData(() => calendarApi.google(), [], { live: false });
  const [clientId, setClientId] = useState<string | null>(null);
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  const v = view.data;
  const save = async (body: { clientId?: string | null; clientSecret?: string | null }) => {
    setBusy(true);
    setSaid(null);
    try {
      await calendarApi.saveGoogle(body);
      setSecret('');
      setClientId(null);
      view.reload();
      setSaid({ tone: 'ok', text: 'Saved in Stitch.' });
    } catch (err) {
      setSaid({ tone: 'crit', text: err instanceof Error ? err.message : 'That didn’t save.' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Google Calendar" action={v ? <Chip tone={v.ready ? 'ok' : 'warn'}>{v.ready ? 'Ready' : 'Not set up'}</Chip> : undefined}>
      <div className="card-body stack">
        <p className="muted small">
          People connect their Google Calendar on Meetings, and meetings they set up for later are added to it. This is Ceexo’s Google sign-in app: create it in Google Cloud (an OAuth client of the Web application kind, with the
          Google Calendar API turned on), then paste its client ID and secret here.
        </p>
        {v && (
          <>
            <dl className="facts">
              <dt>Redirect address</dt>
              <dd className="mono">{v.redirectUri ?? 'Set PUBLIC_URL in EasyPanel first'}</dd>
              <dt>Client ID</dt>
              <dd className="mono">{v.clientId || 'Not set'}</dd>
              <dt>Client secret</dt>
              <dd>{v.secretSet ? 'Saved (sealed: never shown again)' : 'Not set'}</dd>
              <dt>Connected</dt>
              <dd>{v.connected === 1 ? '1 person' : `${v.connected} people`}</dd>
              {v.savedAt && (
                <>
                  <dt>Saved</dt>
                  <dd>
                    {shortWhen(v.savedAt)}
                    {v.savedBy ? ` by ${v.savedBy}` : ''}
                  </dd>
                </>
              )}
            </dl>
            {!v.ready && v.missing.length > 0 && <Notice tone="warn">Still needed: {v.missing.join(', ')}.</Notice>}
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                void save({ ...(clientId !== null ? { clientId: clientId.trim() || null } : {}), ...(secret.trim() ? { clientSecret: secret.trim() } : {}) });
              }}
            >
              <Field label="Client ID" placeholder="1234-abc.apps.googleusercontent.com" value={clientId ?? v.clientId} onChange={(e) => setClientId(e.target.value)} autoComplete="off" spellCheck={false} />
              <Field label={v.secretSet ? 'New client secret' : 'Client secret'} type="password" value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="new-password" hint={v.secretSet ? 'Leave empty to keep the one saved.' : undefined} />
              <div className="row-actions">
                <Button type="submit" variant="primary" disabled={busy || (clientId === null && !secret.trim())}>
                  Save
                </Button>
                {(v.clientId || v.secretSet) && (
                  <Button variant="ghost" disabled={busy} onClick={() => void save({ clientId: null, clientSecret: null })}>
                    Remove
                  </Button>
                )}
              </div>
            </form>
          </>
        )}
        {said && <Notice tone={said.tone}>{said.text}</Notice>}
      </div>
    </Card>
  );
}
